import { useEffect, useState } from 'react';

import { useIntl } from 'src/hooks/intl';
import { DetailedBranchPullRequest } from 'src/sections/repository/sections/branches/types';
import authRequest from 'src/utils/fetch';

import { useBranchUrls } from '../urls';

export type BranchPrListResponse = {
  branch_prs: DetailedBranchPullRequest[] | [];
  hasError: boolean;
  isLoading: boolean;
};

export function useFetchBranchPrList(
  sourceRef: string,
  destRef: string
): BranchPrListResponse {
  const { branchPullRequests } = useBranchUrls();
  const intl = useIntl();
  const [state, setState] = useState<BranchPrListResponse>({
    branch_prs: [],
    hasError: false,
    isLoading: true,
  });

  useEffect(() => {
    if (!destRef) {
      return;
    }

    const fetchData = async () => {
      try {
        let nextUrl: string | null = branchPullRequests(sourceRef, destRef);
        let allPrs: DetailedBranchPullRequest[] = [];

        while (nextUrl) {
          // eslint-disable-next-line no-await-in-loop
          const response: Response = await fetch(authRequest(nextUrl));

          if (!response.ok) {
            setState({
              branch_prs: [],
              hasError: true,
              isLoading: false,
            });
            return;
          }

          // eslint-disable-next-line no-await-in-loop
          const { next, values: pullRequests } = await response.json();

          allPrs = [...allPrs, ...pullRequests];

          if (next) {
            nextUrl = next; // Fetch the next page
          } else {
            nextUrl = null; // No more pages to fetch
          }
        }

        setState({
          branch_prs: allPrs,
          hasError: false,
          isLoading: false,
        });
      } catch {
        setState({
          branch_prs: [],
          hasError: true,
          isLoading: false,
        });
      }
    };

    fetchData();
  }, [sourceRef, destRef, branchPullRequests, intl]);

  return state;
}

export function openPullRequestExists(
  branchPrList: BranchPrListResponse
): boolean {
  const { branch_prs, hasError, isLoading } = branchPrList;

  // allow user to go to create PR view if the branch page pr list has not loaded or errored
  if (isLoading || hasError) {
    return false;
  }

  for (const branch_pr of branch_prs) {
    if (branch_pr.state === 'OPEN') {
      return true;
    }
  }

  return false;
}
